import * as React from "react";
import { Cpu, Network, Wrench, Activity, Terminal, Map, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
const modules = [{
  id: "mod-01",
  title: "Servizi per Aziende",
  description: "Soluzioni strategiche per la mobilità globale dei talenti e la gestione della conformità aziendale internazionale.",
  difficultyLabel: "Corporate",
  difficultyValue: 100,
  icon: Cpu,
  link: "/CompanyServicesPage"
}, {
  id: "mod-02",
  title: "Studenti e Istituti",
  description: "Supporto completo per percorsi accademici transfrontalieri e partnership tra istituzioni educative globali.",
  difficultyLabel: "Academic",
  difficultyValue: 100,
  icon: Network,
  link: "/StudentsAndInstitutesPage"
}, {
  id: "mod-03",
  title: "Visti e Immigrazione",
  description: "Consulenza specialistica per l'ottenimento di visti lavorativi, turistici e permessi di soggiorno permanenti.",
  difficultyLabel: "Legal",
  difficultyValue: 100,
  icon: Wrench,
  link: "/VisasAndImmigrationPage"
}, {
  id: "mod-04",
  title: "Servizi Extragiudiziali",
  description: "Risoluzione di controversie e assistenza amministrativa al di fuori delle sedi giudiziarie ordinarie.",
  difficultyLabel: "Strategic",
  difficultyValue: 100,
  icon: Activity,
  link: "/ExtrajudicialPage"
}, {
  id: "mod-05",
  title: "Mobilità Internazionale",
  description: "Pianificazione logistica e legale per il trasferimento di nuclei familiari e professionisti all'estero.",
  difficultyLabel: "Global",
  difficultyValue: 100,
  icon: Terminal,
  link: "/VisasAndImmigrationPage"
}, {
  id: "mod-06",
  title: "Consulenza e Assistenza",
  description: "Intermediazione professionale e gestione dei rapporti con enti governativi e rappresentanze consolari.",
  difficultyLabel: "Premium",
  difficultyValue: 100,
  icon: Map,
  link: "/ContactsPage"
}];
export default function PracticalLabModules() {
  const [visibleIndices, setVisibleIndices] = React.useState<number[]>([]);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.getAttribute("data-index") || "0", 10);
          setTimeout(() => {
            setVisibleIndices(prev => [...new Set([...prev, index])]);
          }, index * 100);
        }
      });
    }, {
      threshold: 0.1
    });
    const cards = sectionRef.current?.querySelectorAll("[data-card-reveal]");
    cards?.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, []);
  return <section data-section-id="223708" id="direttrici-di-servizio" ref={sectionRef} className="bg-white text-foreground py-16 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-[#00F5FF]/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-[#00F5FF]/10 border border-[#00F5FF]/20 text-primary text-sm font-mono mb-6">
            <span className="size-2 rounded-lg bg-[#00F5FF] animate-pulse" />
            Eccellenza Istituzionale
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight mb-6 text-secondary">
            Direttrici di Servizio
          </h2>
          <p className="text-slate-400 font-default text-lg">
            Un approccio multidisciplinare alla mobilità globale. Offriamo soluzioni integrate per navigare la complessità delle normative internazionali con precisione e affidabilità.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 [perspective:1000px]">
          {modules.map((mod, i) => {
          const Icon = mod.icon;
          const isVisible = visibleIndices.includes(i);
          const isBlueCard = i === 0 || i === 1 || i === 2 || i === 3 || i === 4 || i === 5;
          return <div key={mod.id} data-index={i} data-card-reveal className={cn("transition-all duration-600 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                <Card className={cn("backdrop-blur-[10px] border-[#00F5FF]/30 transition-all duration-300 ease-out overflow-hidden group flex flex-col h-full rounded-lg hover:[transform:rotateX(10deg)_rotateY(10deg)] hover:shadow-[0_0_15px_rgba(0,245,255,0.4)] hover:brightness-110 relative", isBlueCard ? "bg-[#091221]" : "bg-[#1a1a2e]/60")}>
                  {!isBlueCard && <>
                      <div className="absolute inset-0 border border-[#00F5FF]/0 group-hover:border-[#00F5FF]/40 rounded-lg transition-colors duration-300 pointer-events-none" />
                      <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-[#00F5FF]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </>}
                  
                  <CardHeader className="flex-none">
                    <div className="size-12 rounded-lg bg-[#0A0A0F] border border-white/10 flex items-center justify-center mb-4 group-hover:bg-[#00F5FF]/10 group-hover:border-[#00F5FF]/50 transition-all duration-300 ease-in-out">
                      <Icon className="size-6 text-muted-foreground group-hover:text-[#00F5FF] transition-colors duration-300" />
                    </div>
                    <CardTitle className={cn("font-default text-xl tracking-tight text-[#FFC829]", mod.id === "mod-06" && "text-center")}>
                      {mod.title}
                    </CardTitle>
                    <CardDescription className={cn("font-default text-sm leading-relaxed mt-2 text-slate-400", (mod.id === "mod-01" || mod.id === "mod-02" || mod.id === "mod-03") && "text-white")}>
                      {mod.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="flex-1 flex flex-col justify-end mt-4">
                    <div className="space-y-3 bg-white/5 p-4 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="text-slate-500 uppercase tracking-widest">Standard</span>
                        <span className="text-[#00F5FF] font-semibold">{mod.difficultyLabel}</span>
                      </div>
                      <div className="relative overflow-hidden rounded-lg">
                        <Progress value={mod.difficultyValue} className="h-1 bg-white/10 border-none [&>div]:bg-[#00F5FF] [&>div]:transition-all [&>div]:duration-1000 [&>div]:ease-in-out group-hover:[&>div]:translate-x-0" />
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="flex-none pt-2 pb-6">
                    <Button asChild variant="outline" className="w-full bg-[#FFC829] text-primary font-default rounded-lg hover:bg-secondary hover:text-[#0A0A0F] hover:border-[#00F5FF] transition-all duration-300">
                      <Link to={mod.link}>
                        Scopri di più
                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}